import { Extension, mergeAttributes } from "@tiptap/core";
import { Placeholder } from "@tiptap/extensions";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type React from "react";
import { type RefObject, useEffect, useRef, useState } from "react";
import BoldIcon from "@/images/editor-bold.svg?react";
import BulletedListIcon from "@/images/editor-bullet.svg?react";
import ItalicIcon from "@/images/editor-italic.svg?react";
import NumberedListIcon from "@/images/editor-numbered.svg?react";
import UnderlineIcon from "@/images/editor-underline.svg?react";
import "./styles.scss";
import { ListItem, OrderedList } from "@tiptap/extension-list";
import Paragraph from "@tiptap/extension-paragraph";
import Underline from "@tiptap/extension-underline";
import useScrollHiddenTiptap from "@/hooks/useScrollHiddenTiptap";
import { FlattenAndFilterOnPaste } from "./CustomPasteFilterFlattener";

type MenuBarProps = {
  editor: ReturnType<typeof useEditor>;
  menuBarRef: RefObject<HTMLDivElement>;
  showMenuOptions?: boolean;
};

// Custom extensions
const BlurOnEnter = Extension.create({
  name: "blurOnEnter",
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        if (editor.view?.dom) {
          editor.view.dom.blur();
        }
        return true;
      },
    };
  },
});

const BlurOnShiftEnter = Extension.create({
  name: "blurOnShiftEnter",
  addKeyboardShortcuts() {
    return {
      "Shift-Enter": ({ editor }) => {
        if (editor.view?.dom) {
          editor.view.dom.blur();
        }
        return true;
      },
    };
  },
});

const TabIndent = Extension.create({
  name: "tabIndent",
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        if (editor.view) {
          editor.chain().focus().insertContent("\t").run();
        }
        return true;
      },
    };
  },
});

const MenuBar = ({ editor, menuBarRef, showMenuOptions }: MenuBarProps) => {
  if (!editor || !editor.view) {
    return null;
  }

  const createButton = (
    action: () => void,
    isActive: boolean,
    icon: React.ReactNode,
  ) => (
    <button
      type="button"
      onClick={action}
      className={isActive ? "is-active" : ""}
    >
      {icon}
    </button>
  );

  return (
    <div className="menubar-container" ref={menuBarRef}>
      {createButton(
        () => {
          if (editor.view) {
            editor.chain().toggleBold().run();
          }
        },
        editor.isActive("bold"),
        <BoldIcon />,
      )}
      {createButton(
        () => {
          if (editor.view) {
            editor.chain().focus().toggleItalic().run();
          }
        },
        editor.isActive("italic"),
        <ItalicIcon />,
      )}
      {createButton(
        () => {
          if (editor.view) {
            editor.chain().focus().toggleUnderline().run();
          }
        },
        editor.isActive("underline"),
        <UnderlineIcon />,
      )}
      {showMenuOptions && (
        <>
          {createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().toggleBulletList().run();
              }
            },
            editor.isActive("bulletList"),
            <BulletedListIcon />,
          )}
          {createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().toggleOrderedList().run();
              }
            },
            editor.isActive("orderedList"),
            <NumberedListIcon />,
          )}
        </>
      )}
    </div>
  );
};

const CustomEditor = ({
  handleChange,
  handleBlur,
  handleKeyDown,
  editorContent,
  placeholderText,
  editingRef,
  shouldBlurOnEnter,
  shouldBlurOnShiftEnter,
  isDisabled = false,
  isFullWidth = false,
  isFullHeight = false,
  showMenu = false,
  showAdvancedOptions = false,
  forceFocus = false,
  scrollToHiddenAnchor = false,
  id,
}: {
  handleChange: (val: string) => void;
  handleBlur?: () => void;
  handleKeyDown?: (e: React.KeyboardEvent) => void;
  isDisabled?: boolean;
  editorContent: string | undefined;
  placeholderText?: string | undefined;
  editingRef?: RefObject<HTMLDivElement | null>;
  forceFocus?: boolean;
  shouldBlurOnEnter?: boolean;
  shouldBlurOnShiftEnter?: boolean;
  isFullWidth?: boolean;
  isFullHeight?: boolean;
  showMenu?: boolean;
  showAdvancedOptions?: boolean;
  // If true, the editor will scroll to the hidden anchor when the user is typing. This is useful for pages
  // where the editor may be larger than the viewport to position the form right above an element that might be covering it (like a footer, etc).
  scrollToHiddenAnchor?: boolean;
  id?: string;
}) => {
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const menuBarRef = useRef<HTMLDivElement | null>(null);
  const placeholderConfig = Placeholder.configure({
    placeholder: placeholderText ?? "",
  });
  const paragraphConfig = Paragraph.extend({
    parseHTML() {
      return [{ tag: showAdvancedOptions ? "div" : "p" }];
    },
    renderHTML({ HTMLAttributes }) {
      return [
        showAdvancedOptions ? "div" : "p",
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
        0,
      ];
    },
  });
  // NOTE: Remove the next two lines once we support HTML for these fields on the backend.
  const extensions = [
    showMenu
      ? StarterKit.configure({ paragraph: false })
      : StarterKit.configure({ bold: false, italic: false, paragraph: false }),
    placeholderConfig,
    Underline,
    paragraphConfig,
  ];
  const additionalExtensions = [
    showMenu
      ? StarterKit.configure({
          paragraph: false,
          orderedList: false,
          listItem: false,
        })
      : StarterKit.configure({
          bold: false,
          italic: false,
          paragraph: false,
          orderedList: false,
          listItem: false,
        }),
    placeholderConfig,
    Underline,
    OrderedList,
    ListItem,
    paragraphConfig,
    TabIndent,
    FlattenAndFilterOnPaste,
  ];
  const determineExtensions = showAdvancedOptions
    ? additionalExtensions
    : extensions;

  // NOTE: Enable the bottom 'extensions' set up once backend supports HTML. It will reenable all necessary formatting functionality and shortcuts.
  // If you want to test the 'showMenu' bar with all formatting functionality, uncomment the below line and comment the top 'kitConfiguration' variable and 'extensions' array.
  // const extensions = [StarterKit, placeholderConfig, Underline, OrderedList, ListItem];

  // If this prop gets passed, if enter gets pressed, the editor is unselected and we keep the updated value intact.
  if (shouldBlurOnEnter) {
    extensions.push(BlurOnEnter);
  }

  if (shouldBlurOnShiftEnter) {
    extensions.push(BlurOnShiftEnter);
  }

  // Editor set up
  const editor = useEditor({
    extensions: determineExtensions,
    editable: !isDisabled,
    onFocus: () => {
      if (showMenu) {
        setIsMenuVisible(true);
      }
    },
    onBlur: ({ event }) => {
      // Fire off a blur function if needed
      if (handleBlur) {
        // Checks if the editor options are in the menu bar, ignores the passed 'handleBlur' function to avoid the menubar from saving any info in the editor when interacted with.
        if (
          menuBarRef.current?.contains(event.relatedTarget as Element | null)
        ) {
          event.stopPropagation();
          return;
        }
        handleBlur();
      }

      if (showMenu) {
        if (
          menuBarRef.current?.contains(event.relatedTarget as Element | null)
        ) {
          event.stopPropagation();
          return;
        }
        setIsMenuVisible(false);
      }
    },
    onSelectionUpdate: ({ editor }) => {
      if (handleChange && editorContent !== editor.getText()) {
        handleChange(showMenu ? editor.getHTML() : editor.getText());
      }
    },
    content: "",
  });

  useScrollHiddenTiptap({
    editor,
    offset: 100,
    behavior: "smooth",
    debounceDelay: 50,
    scrollToHiddenAnchor,
  });

  useEffect(() => {
    if (editor) {
      const currentContent = showMenu ? editor.getHTML() : editor.getText();
      if (currentContent !== editorContent) {
        editor.commands.setContent(editorContent || "", { emitUpdate: false });
      }

      // Forces the editor to be focused, useful in cases where we want to focus the editor immediately in a component when immediately loaded to the UI
      // Check if editor view is available before focusing
      if (forceFocus && editor.view) {
        editor.commands.focus();
      }
    }
  }, [editor, forceFocus, editorContent, showMenu]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isDisabled);
    }
  }, [editor, isDisabled]);

  // Prevent rendering until the editor is initialized (recommended by Tiptap docs)
  if (!editor) {
    return null;
  }

  return (
    /*If we pass fullwidth, the editor just basically stretches to 100% width of its parent container*/
    <div
      className={`editorContainer ${isFullWidth ? "fw" : ""} ${isFullHeight ? "fh" : ""}`}
    >
      {showMenu && isMenuVisible && (
        <MenuBar
          menuBarRef={menuBarRef as RefObject<HTMLDivElement>}
          editor={editor}
          showMenuOptions={showAdvancedOptions}
        />
      )}
      <EditorContent
        id={id ?? ""}
        className={`${showMenu && isMenuVisible ? "menuOn" : ""}`}
        ref={editingRef}
        onKeyDown={handleKeyDown}
        editor={editor}
      />
      {scrollToHiddenAnchor && <div id="hidden-anchor" />}
    </div>
  );
};

export default CustomEditor;
